import { Button, Flex, IconFont } from '@byecode/ui'
import { DevicePreviewEnum, type DTFile, type FieldProtocol, FILE_MODE, FileConfig, type FileValue } from '@lighthouse/core'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { getAcceptByFileConfig, getUploadLengthLimitByFileConfig } from '../../../../constants'
import { useLanguageContext } from '../../../../contexts'
import { WithStatusFile } from '../../../../types'
import { TriggerNodeFnProps, WithoutPopperFileUploader } from '../../../FileUploader'
import type { FieldBaseProps } from '../../types'
import { UploadBox } from './UploadBox'

interface FileFieldProps extends FieldBaseProps {
    value?: FileValue
    fileField?: FieldProtocol
    fileConfig: FileConfig
}

const SCxContainer = styled.div`
    width: 100%;
    height: 100%;
    /* padding: 0 8px; */
    display: flex;
    align-items: center;
    font-size: var(--font-size-normal);
`

const UploaderAreaBoldTip = styled.span`
    font-weight: 500;
    color: var(--color-gray-600);
`

const FileField: React.FunctionComponent<FileFieldProps> = props => {
    const { readOnly, onChange, value, config, fileField, fileConfig } = props
    const { uploadyOptions, breakPoint } = config
    const { file } = fileConfig
    const mode = file?.mode
    const fileDesign = breakPoint?.fileDesign
    const gridConfig = useMemo(() => {
        if (mode !== FILE_MODE.grid) {
            return undefined
        }
        return {
            gap: fileDesign?.gap,
            borderRadius: fileDesign?.borderRadius,
            itemWidth: fileDesign?.itemWidth,
            maxCount: fileDesign?.maxCount
        }
    }, [fileDesign, mode])
    const { convertTextByLanguage: t } = useLanguageContext()
    const activeFileConfig = config as any
    const isAvatarField = (fileField?.id ?? '').toUpperCase() === 'AVATAR'
    const isVideoField = fileField?.type === 'video'
    const { max: configuredMaxFiles, min: configuredMinFiles } = useMemo(() => getUploadLengthLimitByFileConfig(file), [file])
    const maxFiles = isAvatarField ? 1 : configuredMaxFiles
    const minFiles = isAvatarField ? 1 : configuredMinFiles
    const accept = useMemo(() => {
        if (isAvatarField) {
            return 'image/*'
        }
        if (isVideoField) {
            return 'video/mp4'
        }
        return getAcceptByFileConfig(activeFileConfig, 'file')
    }, [activeFileConfig, isAvatarField, isVideoField])
    const enableItemActions = useMemo(
        () => ({
            canDelete: !readOnly,
            canDownload: fileField?.type !== 'video',
            canPreview: true,
            canRename: !readOnly
        }),
        [fileField?.type, readOnly]
    )
    const handleChange = useCallback(
        (files: DTFile[]) => {
            onChange?.({ type: 'file', value: files })
        },
        [onChange]
    )

    const isOnlyOneFile = useMemo(() => maxFiles === minFiles && minFiles === 1, [maxFiles, minFiles])

    const triggerNodeFn = useCallback(
        (innerFiles: WithStatusFile[], disabled: boolean | undefined, handlers: TriggerNodeFnProps) => {
            if (mode === 'thumbnail') {
                return (
                    <UploadBox
                        title={t('clickOrDragToUpload')}
                        innerFiles={innerFiles}
                        onlyOne={isOnlyOneFile}
                        onPreview={handlers.onPreview}
                        onRemove={handlers.onRemove}
                    />
                )
            }
            if (mode === FILE_MODE.grid) {
                const resolvedItemWidth = gridConfig?.itemWidth
                    ? `${gridConfig.itemWidth.size}${gridConfig.itemWidth.unit || 'px'}`
                    : '80px'
                const borderRadius = gridConfig?.borderRadius != null ? `${gridConfig.borderRadius}px` : '8px'
                return (
                    <div
                        data-field-input
                        data-field-active={false}
                        data-field-disabled={!!disabled}
                        style={{
                            width: resolvedItemWidth,
                            height: resolvedItemWidth,
                            borderRadius,
                            background: 'var(--color-gray-100)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            flexShrink: 0
                        }}
                    >
                        <IconFont type="Add" size={32} color="var(--color-gray-400)" />
                    </div>
                )
            }
            return (
                <Button
                    size="lg"
                    data-field-input
                    data-field-active={false}
                    data-field-disabled={!!disabled}
                    icon={
                        <Flex data-field-icon>
                            <IconFont type="Add" size={16} />
                        </Flex>
                    }
                >
                    {t('clickToUpload')} {maxFiles ? `(${innerFiles.length}/${maxFiles})` : ''}
                </Button>
            )
        },
        [gridConfig, isOnlyOneFile, maxFiles, mode, t]
    )

    return (
        <SCxContainer>
            {uploadyOptions && (
                <WithoutPopperFileUploader
                    hiddenFileList={mode === 'thumbnail' && isOnlyOneFile}
                    previewType={DevicePreviewEnum.DESKTOP}
                    uploadyOptions={uploadyOptions}
                    sortable={file?.enableSortable ?? true}
                    accept={accept}
                    limit={maxFiles}
                    enableItemActions={enableItemActions}
                    disableUpload={readOnly}
                    triggerNodeFn={triggerNodeFn}
                    onChange={handleChange}
                    uploadFileType={fileField?.type === 'video' ? 'video' : 'file'}
                    files={value || []}
                    gridConfig={gridConfig}
                />
            )}
        </SCxContainer>
    )
}

export default FileField
